import { ReactNode } from 'react';
import { Card, Heading, Text } from 'frosted-ui';
import { Checkmark16 } from '@frosted-ui/icons';
import { describeOffer, variantLabel } from '../../utils/variants';
import { controlIsToday, type WizardData } from './wizardTypes';

interface TestSummaryAsideProps {
  data: WizardData;
  checklist: string[];
  /** Unlock offers are new, so the control is the first price, not today's. */
  isUnlock?: boolean;
  /** A seller opening their own offer sees 0 visitors, so this path says why. */
  inWhop?: boolean;
}

function CheckRow({ done, children }: { done: boolean; children: ReactNode }) {
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 20,
          height: 20,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: done ? 'none' : '1.5px solid var(--gray-a8)',
          backgroundColor: done ? 'var(--green-9)' : 'transparent',
          color: 'white',
        }}
      >
        {done && <Checkmark16 style={{ width: 12, height: 12 }} />}
      </span>
      <Text size="2" color={done ? undefined : 'gray'}>{children}</Text>
    </li>
  );
}

/** Right-hand "Your test" + "After you go live" column of the Place it step. */
export default function TestSummaryAside({ data, checklist, isUnlock = false, inWhop = false }: TestSummaryAsideProps) {
  const split = data.variants.map((v) => data.split[v.name] ?? 0).join(' / ');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <Card size="2">
        <Text size="1" color="gray" weight="medium" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Your test
        </Text>
        <Heading size="3" style={{ margin: 'var(--space-2) 0 var(--space-3)' }}>
          {data.name || data.productName}
        </Heading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {data.variants.map((v, i) => (
            <div key={v.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
              <Text size="2" color="gray">
                {variantLabel(v.name)}
                {i === 0 ? (isUnlock ? ' (first price)' : controlIsToday(data) ? " (today's offer)" : '') : ''}
              </Text>
              <Text size="2" weight="medium" className="vara2-tabular" style={{ textAlign: 'right' }}>
                {describeOffer({ ...v, currency: data.currency })}
              </Text>
            </div>
          ))}
        </div>
        <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
          {split} split
        </Text>
      </Card>

      <Card size="2">
        <Text size="1" color="gray" weight="medium" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          After you go live
        </Text>
        <ul style={{ listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {checklist.map((item) => (
            <CheckRow key={item} done={false}>
              {item}
            </CheckRow>
          ))}
        </ul>
        {inWhop && (
          <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
            Your own visits inside Whop don't count.
          </Text>
        )}
      </Card>
    </div>
  );
}

export { CheckRow };
